import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  getQuoteSiteAppearances,
  loadWrongBreedStateError,
  resetState,
  saveNewPetAge,
  saveNewPetBreed,
  saveNewPetEmail,
  saveNewPetName,
  saveNewPetSex,
  saveNewPetType,
  saveNewPetZipCode,
  saveUtmParams,
  validateEmployerId,
} from '../../actions';
import {
  useB2CInfo,
  useKameleoonVisitedPageEvent,
  usePartnerSubId,
} from '../../hooks';
import {
  formatParameters,
  getUtmParams,
  removeSessionStorage,
  setSessionStorage,
} from '../../util';
import { PetGender, PetType, QUOTING_STORAGE } from '../../constants';
import { FigoLoadingOverlay } from '../common/FigoLoadingOverlay';
import { ModalAddPet } from '../common/modalAddPet/ModalAddPet';
import ModalContainer from '../common/ModalContainer';
import { LandingHeader } from './landingHeader';
import { LandingWelcome } from './landingWelcome';
import LandingFooter from './LandingFooter';
import { LandingHowWorks } from './landingHowWorks';
import { AppPresentation } from './appPresentation';
import { LandingQuestions } from './landingQuestions/LandingQuestions';
import { ParentsReview } from './parentsReview/ParentsReview';
import { QuestionsSection } from '../quoting/QuestionsSection';
import { TemporaryMessage } from '../quoting/TemporaryMessage';
import './LandingPage.css';
import { WRONG_BREED_STATE } from '../../utils/customErrors.utils';

const LandingPage = () => {
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting);
  const location = useLocation();
  const parametersRaw =
    useMemo(() => queryString.parse(location.search), [location.search]);
  const parameters =
    useMemo(() => formatParameters(parametersRaw), [parametersRaw]);
  const [visibleGetQuoteModal, setVisibleGetQuoteModal] = useState(false);
  const { b2cToken, logoutB2C } = useB2CInfo();
  const [triggerValidation, setTriggerValidation] = useState(false);
  const { accounts } = useMsal();
  useKameleoonVisitedPageEvent();
  const {
    ageData,
    breedData,
    isAgeDataLoading,
    isBreedDataLoading,
  } = store;
  const urlRedirection = 'https://figopetinsurance.com/employee-benefits/';
  // TODO - This should be coming from a backend endpoint.

  useEffect(() => {
    if (accounts.length && !visibleGetQuoteModal) {
      const currentUrl = `${location.pathname}${location.search}`;
      logoutB2C({ logoutUrl: currentUrl });
    }
  }, [
    accounts.length,
    location.pathname,
    location.search,
    logoutB2C,
    visibleGetQuoteModal,
  ]);

  useEffect(() => {
    if (parameters.empId) {
      dispatch(validateEmployerId(parameters.empId));
      dispatch(getQuoteSiteAppearances(parameters.empId));
    }

    if (!b2cToken && !parameters.empId) {
      window.location.replace(urlRedirection);
    }
  }, [parameters.empId, dispatch, b2cToken]);

  useEffect(() => {
    if (store.employerIdValidated && !store.employerIdValid && !b2cToken) {
      window.location.replace(urlRedirection);
    }
  }, [b2cToken, store.employerIdValid, store.employerIdValidated]);

  const handleCloseModal = useCallback(() => {
    setVisibleGetQuoteModal(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    setVisibleGetQuoteModal(true);
  }, []);

  useEffect(() => {
    dispatch(resetState());
    setSessionStorage({ data: {}, key: QUOTING_STORAGE });
    removeSessionStorage('store.mounted');

    // Pre-populate petform if parameters exists
    const {
      email,
      petName,
      petSex,
      petType,
      petZipCode,
    } = parameters || {};

    if (petName) {
      dispatch(saveNewPetName(petName));
    }

    if (petType) {
      let type = null;

      if (PetType.Dog.name === petType) {
        type = PetType.Dog;
      } else if (PetType.Cat.name === petType) {
        type = PetType.Cat;
      }

      if (type) {
        dispatch(saveNewPetType(type));
      }
    }

    if (petSex) {
      let type = null;

      if (PetGender.Female.name === petSex) {
        type = PetGender.Female;
      } else if (PetGender.Male.name === petSex) {
        type = PetGender.Male;
      }

      if (type) {
        dispatch(saveNewPetSex(type));
      }
    }

    if (email) {
      dispatch(saveNewPetEmail(email));
    }

    if (petZipCode) {
      dispatch(saveNewPetZipCode(petZipCode));
    }

    const petParametersExists = !!(petName
      || petSex
      || petType
      || email
      || petZipCode);

    const fromBreedExpired = !!location.state?.fromBreedExpired;

    setTriggerValidation(petParametersExists && !fromBreedExpired);
    if (petParametersExists && !fromBreedExpired) {
      handleOpenModal();
    }

    const utmParams = getUtmParams(parametersRaw);

    if (Object.keys(utmParams).length) {
      dispatch(saveUtmParams(utmParams));
    }
  }, [dispatch, handleOpenModal, location.state, parameters, parametersRaw]);

  // `petAge` populate age field if found on query params
  useEffect(() => {
    if (isAgeDataLoading || !parameters.petAge) {
      return;
    }

    const ageFound = ageData
      ?.find?.(({ Description }) => Description === parameters.petAge);

    if (ageFound) {
      dispatch(saveNewPetAge(ageFound.Description));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ageData, isAgeDataLoading]);

  // `petBreed` populates breed field if found on query params
  useEffect(() => {
    if (isBreedDataLoading || !parameters.petBreed) {
      return;
    }

    const breedFound = breedData
      .find(({ BreedName }) => BreedName === parameters.petBreed);

    if (breedFound) {
      dispatch(saveNewPetBreed({
        petBreed: breedFound.BreedName,
        petBreedId: breedFound.Id,
      }));
    } else if (parameters.petBreed === WRONG_BREED_STATE) {
      dispatch(loadWrongBreedStateError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, breedData, isBreedDataLoading]);

  usePartnerSubId();

  return (
    <main className="Landing-page">
      <LandingHeader onGetQuote={handleOpenModal} />

      <LandingWelcome onGetQuote={handleOpenModal} />

      <LandingHowWorks />

      <LandingQuestions />

      <AppPresentation />

      <ParentsReview />

      <TemporaryMessage sticky />

      <QuestionsSection isQuote />

      <LandingFooter />

      <ModalContainer
        cancelable
        classCloseIcon="Landing-page-close-icon-modal"
        handleOutClick={handleCloseModal}
        idPrefix="getQuote"
        show={visibleGetQuoteModal}
      >
        <ModalAddPet
          getQuote
          handleCloseModalAddPet={handleCloseModal}
          show={visibleGetQuoteModal}
          validateOnMount={triggerValidation}
        />
      </ModalContainer>

      <FigoLoadingOverlay visible={store.validatingEmployerId} />
    </main>
  );
};

export { LandingPage };
